<template>
  <ContentWrap title="设施关联管理">
    <!-- 目标选择器 -->
    <el-form :model="targetForm" :inline="true" label-width="80px" class="mb-15px">
      <el-form-item label="目标类型" prop="targetType">
        <el-select v-model="targetForm.targetType" placeholder="请选择目标类型" class="!w-180px">
          <el-option label="酒店" value="HOTEL" />
          <el-option label="房型" value="ROOM_TYPE" />
          <el-option label="房间" value="ROOM" />
        </el-select>
      </el-form-item>
      <el-form-item v-if="targetForm.targetType === 'ROOM'" label="所属店铺" prop="shopId">
        <el-select v-model="targetForm.shopId" placeholder="请选择店铺" filterable class="!w-180px">
          <el-option v-for="item in shopList" :key="item.id" :label="item.name" :value="item.id" />
        </el-select>
      </el-form-item>
      <el-form-item label="目标ID" prop="targetId">
        <el-select
          v-if="targetForm.targetType === 'HOTEL'"
          v-model="targetForm.targetId"
          placeholder="请选择酒店"
          clearable
          filterable
          class="!w-180px"
          @keyup.enter="handleQuery"
        >
          <el-option
            v-for="item in shopList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <el-select
          v-else-if="targetForm.targetType === 'ROOM_TYPE'"
          v-model="targetForm.targetId"
          placeholder="请选择房型"
          clearable
          filterable
          class="!w-180px"
          @keyup.enter="handleQuery"
        >
          <el-option
            v-for="item in roomTypeList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
        <el-select
          v-else-if="targetForm.targetType === 'ROOM'"
          v-model="targetForm.targetId"
          placeholder="请选择房间"
          clearable
          filterable
          class="!w-180px"
          :disabled="!targetForm.shopId"
        >
          <el-option v-for="item in roomList" :key="item.id" :label="`${item.roomNo}（${item.floor ?? '-'}层）`" :value="item.id" />
        </el-select>
        <el-select v-else placeholder="请先选择目标类型" disabled class="!w-180px" />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery"><Icon icon="ep:search" class="mr-5px" /> 查询关联</el-button>
        <el-button
          type="primary"
          plain
          @click="openAddDialog"
          v-hasPermi="['hotel:facility-relation:update']"
          :disabled="!targetForm.targetType || !targetForm.targetId"
        >
          <Icon icon="ep:plus" class="mr-5px" /> 添加设施
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 关联列表 -->
  <ContentWrap>
    <el-table v-loading="loading" :data="list" :stripe="true">
      <el-table-column label="设施名称" align="center" prop="facilityName" min-width="150" />
      <el-table-column label="是否免费" align="center" prop="isFree" width="100">
        <template #default="scope">
          <el-tag :type="scope.row.isFree === 1 ? 'success' : 'warning'">
            {{ scope.row.isFree === 1 ? '免费' : '收费' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="创建时间" align="center" prop="createTime" width="180">
        <template #default="scope">
          <span>{{ scope.row.createTime }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" width="120">
        <template #default="scope">
          <el-button
            link
            type="danger"
            @click="handleDelete(scope.row.id)"
            v-hasPermi="['hotel:facility-relation:delete']"
          >
            删除关联
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <!-- 添加设施弹窗 -->
  <Dialog :title="'添加设施关联'" v-model="dialogVisible" width="500px">
    <el-form ref="formRef" :model="formData" :rules="formRules" label-width="100px" v-loading="formLoading">
      <el-form-item label="设施" prop="facilityId">
        <el-select v-model="formData.facilityId" placeholder="请选择设施" class="!w-1/1">
          <el-option
            v-for="item in availableFacilities"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="是否免费" prop="isFree">
        <el-radio-group v-model="formData.isFree">
          <el-radio :value="1">免费</el-radio>
          <el-radio :value="0">收费</el-radio>
        </el-radio-group>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="submitForm" type="primary" :disabled="formLoading">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import * as facilityRelationApi from '@/api/hotel/facilityRelation'
import * as facilityApi from '@/api/hotel/facility'
import { getShopSimpleList } from '@/api/hotel/platformShop'
import { getSpuSimpleList } from '@/api/mall/product/spu'
import { listAllRooms } from '@/api/hotel/room'
import { watch } from 'vue'

defineOptions({ name: 'HotelFacilityRelation' })

const message = useMessage()

const loading = ref(false)
const list = ref<facilityRelationApi.FacilityRelationVO[]>([])

const targetForm = reactive({
  targetType: 'HOTEL',
  shopId: undefined as string | number | undefined,
  targetId: undefined as string | number | undefined
})

// 下拉数据源
const shopList = ref<{ id: string | number; name: string }[]>([])
const roomTypeList = ref<{ id: string | number; name: string }[]>([])
const roomList = ref<any[]>([])

const loadShopList = async () => {
  try {
    shopList.value = await getShopSimpleList()
  } catch {
    shopList.value = []
  }
}

const loadRoomTypeList = async () => {
  try {
    roomTypeList.value = await getSpuSimpleList()
  } catch {
    roomTypeList.value = []
  }
}

// 监听目标类型变化，自动加载对应列表并重置目标ID
watch(
  () => targetForm.targetType,
  (newType) => {
    targetForm.targetId = undefined
    targetForm.shopId = undefined
    if (newType === 'HOTEL') {
      loadShopList()
    } else if (newType === 'ROOM_TYPE') {
      loadRoomTypeList()
    } else if (newType === 'ROOM') {
      loadShopList()
    }
  },
  { immediate: true }
)

watch(
  () => targetForm.shopId,
  async (shopId) => {
    targetForm.targetId = undefined
    roomList.value = []
    if (!shopId || targetForm.targetType !== 'ROOM') return
    const data = await listAllRooms()
    const rows = Array.isArray(data) ? data : data?.list || []
    roomList.value = rows.filter((item: any) => String(item.shopId) === String(shopId))
  }
)

// 查询关联设施列表
const handleQuery = async () => {
  if (!targetForm.targetType || !targetForm.targetId) {
    message.warning('请选择目标类型和目标')
    return
  }
  loading.value = true
  try {
    const data = await facilityRelationApi.listByTarget(targetForm.targetType, targetForm.targetId)
    list.value = data || []
  } catch {
    list.value = []
  } finally {
    loading.value = false
  }
}

// 删除关联
const handleDelete = async (id: string | number) => {
  try {
    await message.delConfirm()
    await facilityRelationApi.deleteRelation(id)
    message.success('删除成功')
    await handleQuery()
  } catch {}
}

// 添加设施弹窗
const dialogVisible = ref(false)
const formLoading = ref(false)
const formRef = ref()
const formData = ref({
  facilityId: undefined as string | number | undefined,
  isFree: 0
})
const formRules = reactive({
  facilityId: [{ required: true, message: '请选择设施', trigger: 'change' }]
})

const allFacilities = ref<facilityApi.FacilityVO[]>([])
const availableFacilities = computed(() => {
  const usedIds = list.value.map((item) => item.facilityId)
  return allFacilities.value.filter((f) => !usedIds.includes(f.id))
})

const openAddDialog = async () => {
  formData.value = { facilityId: undefined, isFree: 0 }
  formRef.value?.resetFields()
  // 获取全部设施
  try {
    allFacilities.value = await facilityApi.listAll()
  } catch {
    allFacilities.value = []
  }
  dialogVisible.value = true
}

// 提交表单（单个添加，实际使用 batchSave）
const submitForm = async () => {
  await formRef.value.validate()
  formLoading.value = true
  try {
    await facilityRelationApi.batchSave({
      targetType: targetForm.targetType,
      targetId: targetForm.targetId as string | number,
      relations: [
        {
          facilityId: formData.value.facilityId!,
          isFree: formData.value.isFree
        }
      ]
    })
    message.success('添加成功')
    dialogVisible.value = false
    await handleQuery()
  } finally {
    formLoading.value = false
  }
}
</script>
